import { useEffect, useState, useCallback, type ChangeEvent, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { galleryApi, resolveImageUrl, type GalleryPost } from "@/lib/api";
import { type FormStatus } from "@/lib/types";
import { formatDate } from "@/lib/date";
import Footer from "@/pages/home/components/Footer";
import ScrollReveal from "@/components/ScrollReveal";

export default function GalleryAdminPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<GalleryPost[]>([]);
  const [fetching, setFetching] = useState(true);
  const [fetchError, setFetchError] = useState("");

  const [editingPost, setEditingPost] = useState<GalleryPost | null>(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("현장");
  const [content, setContent] = useState("");
  const [author, setAuthor] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState("");

  const [status, setStatus] = useState<FormStatus>("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const loadPosts = useCallback(async () => {
    setFetching(true);
    setFetchError("");
    try {
      const { data } = await galleryApi.list();
      setPosts(data || []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "목록을 불러오지 못했습니다.";
      setFetchError(msg);
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const resetForm = () => {
    setTitle("");
    setCategory("현장");
    setContent("");
    setAuthor("");
    setFile(null);
    setPreview("");
    setStatus("idle");
    setErrorMsg("");
    setEditingPost(null);
  };

  const handleEdit = (post: GalleryPost) => {
    setEditingPost(post);
    setTitle(post.title);
    setCategory(post.category);
    setContent(post.content);
    setAuthor(post.author);
    setFile(null);
    setPreview(post.image);
    setStatus("idle");
    setErrorMsg("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    setFile(selected);
    if (selected) {
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result as string);
      reader.readAsDataURL(selected);
    } else {
      setPreview(editingPost ? editingPost.image : "");
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingPost && !file) {
      setStatus("error");
      setErrorMsg("이미지 파일을 선택해 주세요.");
      return;
    }
    if (!title.trim()) {
      setStatus("error");
      setErrorMsg("제목을 입력해 주세요.");
      return;
    }

    setStatus("submitting");
    setErrorMsg("");

    try {
      let imageUrl = editingPost ? editingPost.image : "";
      if (file) {
        imageUrl = await galleryApi.uploadImage(file);
      }

      const payload = {
        title: title.trim(),
        category,
        content: content.trim(),
        author: author.trim() || "관리자",
        image: imageUrl,
      };

      if (editingPost) {
        await galleryApi.update(editingPost.id, payload);
      } else {
        await galleryApi.create(payload);
      }

      setStatus("success");
      resetForm();
      loadPosts();
      setTimeout(() => setStatus("idle"), 2000);
    } catch (err: unknown) {
      setStatus("error");
      const msg = err instanceof Error ? err.message : editingPost ? "수정에 실패했습니다." : "등록에 실패했습니다.";
      setErrorMsg(msg);
    }
  };

  const handleDelete = async (post: GalleryPost) => {
    if (!confirm(`"${post.title}" 항목을 삭제하시겠습니까?`)) return;
    try {
      await galleryApi.remove(post.id);

      if (editingPost?.id === post.id) {
        resetForm();
      }
      loadPosts();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "삭제에 실패했습니다.");
    }
  };

  const isEditing = editingPost !== null;

  return (
    <>
      <main className="min-h-screen bg-background-50 pt-20">
        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-6 md:px-10">
            <div className="mb-6">
              <button
                onClick={() => navigate("/")}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-background-200 hover:border-primary-400 hover:bg-primary-50 text-foreground-600 hover:text-primary-700 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                홈으로 돌아가기
              </button>
            </div>
            <div className="text-center mb-14">
              <ScrollReveal direction="up" delay={100}>
                <div className="inline-flex items-center gap-2 text-primary-700 text-xs tracking-[0.3em] uppercase font-medium mb-4">
                  <span className="w-8 h-px bg-primary-500"></span>
                  Gallery Admin
                </div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
                  작업 이미지 관리
                </h1>
                <p className="mt-4 text-foreground-700 text-base max-w-xl mx-auto">
                  작업 현장 사진을 등록하고 관리하는 페이지입니다.
                  등록한 이미지는 메인 페이지 갤러리 섹션에 표시됩니다.
                </p>
              </ScrollReveal>
            </div>

            <div className={`rounded-lg border p-6 md:p-10 mb-12 transition-colors ${isEditing ? "bg-primary-50/40 border-primary-200" : "bg-background-100/60 border-background-200/70"}`}>
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                {isEditing ? "이미지 수정" : "새 이미지 등록"}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-2">
                      제목 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      required
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      type="text"
                      placeholder="예) 2026년 3월 예천 현장 원목칩 파쇄"
                      className="w-full h-12 px-4 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-2">
                      카테고리
                    </label>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="w-full h-12 px-4 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 pr-8"
                    >
                      <option value="현장">현장</option>
                      <option value="제품">제품</option>
                      <option value="품질">품질</option>
                      <option value="정비">정비</option>
                      <option value="특수">특수</option>
                      <option value="샘플">샘플</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-2">
                      작성자
                    </label>
                    <input
                      value={author}
                      onChange={(e) => setAuthor(e.target.value)}
                      type="text"
                      placeholder="예) 우공이산"
                      className="w-full h-12 px-4 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-2">
                      이미지 파일 {!isEditing && <span className="text-accent-600">*</span>}
                    </label>
                    <input
                      required={!isEditing}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="w-full h-12 px-4 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 cursor-pointer"
                    />
                    {isEditing && (
                      <p className="mt-1.5 text-xs text-foreground-500">
                        새 이미지로 교체할 때만 선택해 주세요.
                      </p>
                    )}
                  </div>
                </div>

                {preview && (
                  <div className="rounded-lg overflow-hidden border border-background-200/70 h-48 md:h-64 w-full">
                    <img
                      src={resolveImageUrl(preview)}
                      alt="미리보기"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-foreground-800 mb-2">
                    내용 / 설명
                  </label>
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    rows={4}
                    maxLength={500}
                    placeholder="현장 위치, 작업 규모, 사용 장비 등을 간략히 설명해 주세요."
                    className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-400 resize-none"
                  ></textarea>
                </div>

                {status === "success" && (
                  <div className="p-4 rounded-md bg-primary-50 border border-primary-200 text-primary-800 text-sm flex items-start gap-2">
                    <i className="ri-check-double-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    {isEditing ? "이미지가 성공적으로 수정되었습니다." : "이미지가 성공적으로 등록되었습니다."}
                  </div>
                )}
                {status === "error" && (
                  <div className="p-4 rounded-md bg-accent-100 border border-accent-300 text-accent-900 text-sm flex items-start gap-2">
                    <i className="ri-error-warning-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    {errorMsg || "처리에 실패했습니다."}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="px-8 py-3.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-medium text-sm tracking-wide transition-colors cursor-pointer whitespace-nowrap disabled:opacity-70 flex items-center justify-center gap-2"
                  >
                    {status === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                        {isEditing ? "수정 중..." : "업로드 중..."}
                      </>
                    ) : isEditing ? (
                      <>
                        수정 완료
                        <i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>
                      </>
                    ) : (
                      <>
                        이미지 등록
                        <i className="ri-upload-cloud-2-line w-4 h-4 flex items-center justify-center"></i>
                      </>
                    )}
                  </button>
                  {isEditing ? (
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-6 py-3.5 rounded-md border border-background-300 text-foreground-700 hover:bg-background-100 font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
                    >
                      수정 취소
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-6 py-3.5 rounded-md border border-background-300 text-foreground-700 hover:bg-background-100 font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
                    >
                      초기화
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-bold text-foreground-950">
                  등록된 이미지 목록
                </h2>
                <span className="text-sm text-foreground-500">
                  총 {posts.length}개
                </span>
              </div>

              {fetching && (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 w-8 h-8 flex items-center justify-center"></i>
                  <span className="text-foreground-500 text-sm">목록을 불러오는 중...</span>
                </div>
              )}

              {!fetching && fetchError && (
                <div className="text-center py-10">
                  <div className="inline-flex flex-col items-center gap-3 p-6 rounded-lg bg-accent-50 border border-accent-200">
                    <i className="ri-error-warning-line text-2xl text-accent-600 w-6 h-6 flex items-center justify-center"></i>
                    <p className="text-accent-800 text-sm">{fetchError}</p>
                    <button
                      onClick={loadPosts}
                      className="px-4 py-2 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium cursor-pointer transition-colors"
                    >
                      다시 시도
                    </button>
                  </div>
                </div>
              )}

              {!fetching && !fetchError && posts.length === 0 && (
                <div className="text-center py-10 rounded-lg bg-background-100 border border-background-200">
                  <i className="ri-image-line text-3xl text-foreground-400 w-8 h-8 flex items-center justify-center mx-auto mb-3"></i>
                  <p className="text-foreground-600 text-sm">등록된 이미지가 없습니다.</p>
                </div>
              )}

              {!fetching && !fetchError && posts.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {posts.map((post) => (
                    <div
                      key={post.id}
                      className={`rounded-lg border overflow-hidden bg-background-50 flex flex-col transition-colors ${editingPost?.id === post.id ? "border-primary-400 ring-2 ring-primary-200" : "border-background-200/70"}`}
                    >
                      <div className="h-44 w-full overflow-hidden bg-background-100">
                        <img
                          src={resolveImageUrl(post.image)}
                          alt={post.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="p-4 flex flex-col flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-medium bg-secondary-100 text-secondary-800 whitespace-nowrap">
                            {post.category}
                          </span>
                          <span className="text-[11px] text-foreground-400">
                            {formatDate(post.created_at)}
                          </span>
                        </div>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1 line-clamp-1">
                          {post.title}
                        </h3>
                        <p className="text-xs text-foreground-500 line-clamp-2 mb-3 flex-1">
                          {post.content || "설명 없음"}
                        </p>
                        <div className="flex items-center justify-between pt-3 border-t border-background-100">
                          <span className="text-xs text-foreground-400">{post.author}</span>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => handleEdit(post)}
                              className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer flex items-center gap-1"
                            >
                              <i className="ri-edit-line w-3 h-3 flex items-center justify-center"></i>
                              수정
                            </button>
                            <button
                              onClick={() => handleDelete(post)}
                              className="text-xs text-accent-600 hover:text-accent-700 font-medium cursor-pointer flex items-center gap-1"
                            >
                              <i className="ri-delete-bin-line w-3 h-3 flex items-center justify-center"></i>
                              삭제
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}